import type { FC, ReactNode } from 'react'
import type { TabKey } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useRouter } from 'next/router'
import { TabBar } from '@/components/app/tab-bar'
import { BrandMark } from '@/components/app/brand-mark'

interface AppFrameProps {
    activeTab: TabKey | null
    children: ReactNode
}

type FrameContextValue = {
    overlayRoot: HTMLElement | null
    scroller: HTMLElement | null
    cover: (from?: HTMLElement | null) => Promise<void>
}

const FrameContext = createContext<FrameContextValue>({
    overlayRoot: null,
    scroller: null,
    cover: async () => {}
})

export const useFrame = (): FrameContextValue => useContext(FrameContext)

const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const AppFrame: FC<AppFrameProps> = ({ activeTab, children }): ReactNode => {
    const router = useRouter()
    const frameRef = useRef<HTMLDivElement>(null)
    const curtainRef = useRef<HTMLDivElement>(null)
    const coveredRef = useRef(false)
    const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null)
    const [scroller, setScroller] = useState<HTMLElement | null>(null)

    // Fills the frame with a circle growing from the tapped element, then resolves so the caller can navigate
    const cover = useCallback((from?: HTMLElement | null) => new Promise<void>(resolve => {
        const frame = frameRef.current
        const curtain = curtainRef.current

        if (!frame || !curtain) return resolve()

        const frameRect = frame.getBoundingClientRect()
        const origin = from?.getBoundingClientRect()
        const x = origin ? origin.left + origin.width / 2 - frameRect.left : frameRect.width / 2
        const y = origin ? origin.top + origin.height / 2 - frameRect.top : frameRect.height / 2
        const reduce = prefersReducedMotion()

        curtain.style.transition = 'none'
        curtain.style.visibility = 'visible'
        curtain.style.opacity = '1'
        curtain.style.clipPath = `circle(0px at ${x}px ${y}px)`
        void curtain.offsetWidth
        curtain.style.transition = reduce ? 'none' : 'clip-path 650ms cubic-bezier(0.65, 0, 0.35, 1)'
        curtain.style.clipPath = `circle(160% at ${x}px ${y}px)`
        coveredRef.current = true

        window.setTimeout(resolve, reduce ? 0 : 620)
    }), [])

    useEffect(() => {
        const reveal = () => {
            const curtain = curtainRef.current

            scroller?.scrollTo({ top: 0, behavior: 'instant' })

            if (!curtain || !coveredRef.current) return

            coveredRef.current = false
            curtain.style.transition = 'opacity 450ms ease 150ms'
            curtain.style.opacity = '0'

            window.setTimeout(() => {
                if (!coveredRef.current) curtain.style.visibility = 'hidden'
            }, 650)
        }

        router.events.on('routeChangeComplete', reveal)
        router.events.on('routeChangeError', reveal)

        return () => {
            router.events.off('routeChangeComplete', reveal)
            router.events.off('routeChangeError', reveal)
        }
    }, [router.events, scroller])

    const value = useMemo(() => ({ overlayRoot, scroller, cover }), [overlayRoot, scroller, cover])

    return (
        <FrameContext.Provider value={value}>
            <div className='min-h-dvh w-full md:flex md:items-center md:justify-center md:bg-[radial-gradient(60rem_40rem_at_15%_10%,rgba(242,107,58,0.16),transparent_60%),radial-gradient(50rem_40rem_at_90%_90%,rgba(26,22,19,0.08),transparent_60%)] md:p-6'>
                <div
                    ref={frameRef}
                    className='relative isolate h-dvh w-full overflow-hidden bg-background md:h-[min(880px,calc(100dvh-3rem))] md:w-[410px] md:rounded-[52px] md:border-[10px] md:border-ink md:shadow-[0_40px_90px_-30px_rgba(26,22,19,0.55),0_0_0_1px_rgba(26,22,19,0.08)]'
                >
                    <div
                        ref={setScroller}
                        id='app-scroller'
                        className='no-scrollbar h-full overflow-y-auto overscroll-contain scroll-smooth'
                    >
                        {children}
                    </div>

                    {activeTab && <TabBar active={activeTab} />}

                    <div ref={setOverlayRoot} className='pointer-events-none absolute inset-0 z-50' />

                    <div
                        ref={curtainRef}
                        aria-hidden='true'
                        className='pointer-events-none absolute inset-0 z-[60] flex items-center justify-center bg-ink'
                        style={{ visibility: 'hidden', opacity: 0 }}
                    >
                        <BrandMark pulse className='size-16 bg-ink-soft' />
                    </div>
                </div>
            </div>
        </FrameContext.Provider>
    )
}
